import React, { useRef, RefObject, forwardRef } from "react";
import { createRoot } from "react-dom/client";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { message } from "antd";
// import { Document } from "@react-pdf/renderer";

// Types
type PdfContentProps = {
  children?: React.ReactNode;
  fileName?: string;
  inspectionId: string;
};

// Document wrapper
const MyDocument: React.FC<PdfContentProps> = ({ children }) => (
  // <Document>{children}</Document>
  <>{children}</>
);

// Forward ref component
const PdfContent = forwardRef<HTMLDivElement, PdfContentProps>(
  (props, ref) => (
    <div ref={ref}>
      <MyDocument {...props} />
    </div>
  )
);

PdfContent.displayName = "PdfContent";

// Hook
const useDownloadPDF = () => {
  const elementRef = useRef<HTMLDivElement>(null);

  const downloadPDF = async (
    content: RefObject<HTMLDivElement | null>,
    fileName: string = "download.pdf",
    inspectionId: string | '',
  ): Promise<void> => {
    if (!content.current) return;

    const pdf = new jsPDF("p", "mm", "a4");
    const pageHeight = pdf.internal.pageSize.height;
    let yPosition = 0;

    const elements = content.current.querySelectorAll<HTMLElement>(`.i${inspectionId}-pdf-page`);
    const header = content.current.querySelector<HTMLElement>(`.i${inspectionId}-pdf-header`);
    const firstFooter = content.current.querySelector<HTMLElement>(".pdf-first-footer");
    const footer = content.current.querySelector<HTMLElement>(".pdf-footer");

    // Header
    if (header) {
      const canvasHeader = await html2canvas(header);
      const imgData = canvasHeader.toDataURL("image/png");
      const imgHeight = (canvasHeader.height * 210) / canvasHeader.width;

      pdf.addImage(imgData, "PNG", 0, yPosition, 210, imgHeight);
    }

    // First Footer
    if (firstFooter) {
      const canvasFooterHeader = await html2canvas(firstFooter);
      const imgFooterHeader = canvasFooterHeader.toDataURL("image/png");
      const imgHeight =
        (canvasFooterHeader.height * 210) / canvasFooterHeader.width;

      pdf.addImage(imgFooterHeader, "PNG", 0, pageHeight - 27, 210, imgHeight);
    }

    yPosition += pageHeight;

    for (let i = 0; i < elements.length; i++) {
      const canvas = await html2canvas(elements[i]);
      const imgData = canvas.toDataURL("image/png");
      const imgHeight = (canvas.height * 210) / canvas.width;

      if (yPosition + imgHeight > pageHeight) {
        pdf.addPage();
        yPosition = 0;
      }

      pdf.addImage(imgData, "PNG", 0, yPosition, 210, imgHeight);

      // Footer
      if (footer) {
        const canvasFooter = await html2canvas(footer);
        const imgFooter = canvasFooter.toDataURL("image/png");
        const footerHeight =
          (canvasFooter.height * 210) / canvasFooter.width;

        pdf.addImage(imgFooter, "PNG", 0, pageHeight - 27, 210, footerHeight);
      }

      yPosition += imgHeight;
    }

    pdf.save(fileName);
  };

  const downloadPDFWithContent = async (props: PdfContentProps) => {
    const tempContainer = document.createElement("div");
    document.body.appendChild(tempContainer);

    const root = createRoot(tempContainer);
    root.render(<PdfContent ref={elementRef} {...props} />);

    setTimeout(async () => {
      try {
        await downloadPDF(elementRef, props.fileName || "download.pdf", props.inspectionId);
      } catch (error) {
        console.error("Failed to generate PDF", error);
        message.error("Failed to generate PDF. Please try again.");
      } finally {
        root.unmount();
        document.body.removeChild(tempContainer);
      }
    }, 2000);
  };

  return { elementRef, downloadPDF, downloadPDFWithContent };
};

export default useDownloadPDF;